<template>
  <section class="main">
    <h1 class="intro">{{ $t("MY_APPS") }} (<a href="https://www.youtube.com/watch?v=5g6-JeZff8I" target="_blank">{{ $t('VIDEO_TUTORIAL') }}</a>)</h1>
    <div class="row">
      <div>
        <p class="g-text">
          {{ $t("MYAPPSTORE_DESC") }}
          <b class="email"
            >Email ID:
            {{ $store.getters.devices[0] && $store.getters.devices[0].email }}
          </b>
        </p>
        <br/>
        <html-with-links
          tag="p"
          class="g-text"
          :html="$t('CUSTOMER_APPS_SHARING_NOTICE')"
        />
        <br/>
        <html-with-links
          tag="p"
          class="g-text"
          :html="$t('APP_IMPORT_EXAMPLE_NOTICE',{href:`https://${BRAND_CONFIG.domain}/my/apps?importUrl=https://example.com/file.ipa`})"
        />
        <div class="header">
          <h2 class="header__title">{{ $t("YOUR APPS") }}</h2>
          <span class="header__storage">
            <template v-for="(limit, idx) in limits">
              {{ limit.label }}: {{ prettysize(limit.usedBytes) }} / {{ prettysize(limit.limitBytes) }}<br :key="idx">
            </template>

            <html-with-links
              v-if="!hasPlus"
              tag="span"
              style="font-size: 12px;"
              :html="'(' + $t('UPGRADE_MY_APPSTORE_STORAGE',
                { href: '/my/plus'}) + ')'"
              />
          </span>
        </div>

        <v-filters v-model="search.filters" />

        <div v-infinite-scroll="loadNextSearchPage">
          <app
            v-for="app in search.result"
            :app="app"
            :key="app.id"
            class="main__app"
            :isInStore="true"
            @deleted="onDeleted(app)"
          />
        </div>

        <v-loader
          v-if="!search.pagination.isLastPage"
          class="loading-spinner"
          :class="{
            'loading-spinner_visible': search.isSearching
          }"
        />
      </div>
      <div class="upload">
        <form class="upload__box">
          <input
            @change="newFilesHandler"
            class="upload__input"
            ref="upload"
            type="file"
            multiple
          />
          <v-button type="primary" @click.native.prevent="select" class="upload__button">
            {{ $t("UPLOAD_IPA_FILES") }}
          </v-button>
          <v-button
            @click.native.prevent="uploadByUrlWithPrompt"
            :loading="uploadingByUrl"
            type="primary"
            class="upload__button"
          >{{ $t("UPLOAD_BY_URL") }}</v-button>
          <span v-if="uploads.length" class="upload__num">
            {{ $t("FILES_SELECTED", { count: uploads.length }) }}
          </span>
          <ul>
            <li v-for="upload in uploads" :key="upload.id" class="upload__item">
              <template v-if="!upload.status">
                <span class="upload__title">{{ upload.name }}</span>
                <span class="upload__progress-wrapper">
                  <span class="upload__progress">
                    <span
                      :style="{ width: upload.progress + '%' }"
                      class="upload__progress-inner"
                    ></span>
                  </span>
                  {{ upload.progress }}%
                </span>
              </template>
              <template v-else>
                <span
                  class="upload__status"
                  :class="{ upload__status_error: upload.error }"
                >
                  {{ upload.status }}
                </span>
                <span class="upload__date">
                  {{ upload.date }}
                  <img
                    v-if="upload.finished"
                    @click="deleteJob(upload.id)"
                    class="upload__delete"
                    src="@/assets/icon-delete.svg"
                  />
                </span>
              </template>
            </li>
          </ul>
        </form>
        <p class="help g-text">
          <img class="help__icon" src="@/assets/icon-info.svg" />
          <html-with-links
            tag="span"
            class="help__text"
            :html="
              $t('CONFIGURE_FEATURES_AT_PAGE', {
                configureLink: '/my/configure',
              })
            "
          ></html-with-links>
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import App from '@/components/App';
import prettysize from 'prettysize';
import HtmlWithLinks from '@/components/HtmlWithLinks';
import VButton from '@/components/Button';
import { mapGetters, mapActions, mapState } from 'vuex';
import infiniteScroll from '@/mixins/infiniteScroll';
import VFilters from './components/VFilters.vue';

export default {
  mixins: [infiniteScroll],
  components: {
    App, HtmlWithLinks, VButton, VFilters,
  },
  data() {
    return {
      stats: {},
      uploads: [],
      uploadingByUrl: false,
      search: {
        apiMethod: 'getIpas',
      },
    };
  },
  computed: {
    ...mapState(['customerProfile']),
    ...mapGetters(['hasPlus']),
    title() {
      return this.$t('MY_APPS_TITLE');
    },
    limits() {
      if (!this.customerProfile.usage) {
        return [];
      }

      return [
        {
          label: this.$t('PRIVATE_APPS'),
          usedBytes: this.customerProfile.usage.private_ipa_storage.used_bytes,
          limitBytes: this.customerProfile.usage.private_ipa_storage.limit_bytes,
        },
        {
          label: this.$t('PUBLIC_APPS'),
          usedBytes: this.customerProfile.usage.public_ipa_storage.used_bytes,
          limitBytes: this.customerProfile.usage.public_ipa_storage.limit_bytes,
        },
      ];
    },
    shouldScheduleGetJobs() {
      return this.uploads.some(upload => !upload.finished);
    },
  },
  methods: {
    prettysize,
    ...mapActions(['getCustomerProfile']),
    select() {
      this.$refs.upload.click();
    },
    generateId() {
      return [...Array(40)].map(() => Math.random().toString(36)[2]).join('');
    },
    newFilesHandler() {
      Array.from(this.$refs.upload.files).forEach(async (file) => {
        const id = this.generateId();
        const app = {
          id,
          name: file.name,
          progress: 0,
          status: '',
          date: '',
          finished: false,
          error: false,
        };
        this.uploads.unshift(app);

        try {
          const response = await this.$api.uploadApp({ file, id }, (e) => {
            app.progress = Math.round((e.loaded * 100) / e.total);
          });

          if (!response.success) {
            app.status = this.$t('UPLOAD_FAILED_WITH_REASON', { reason: response.errors[0].translated });
            app.error = true;
          } else {
            app.status = this.$t('REQUESTING_VALIDATION');
            this.tryScheduleGetJobs();
          }
        } catch {
          app.status = this.$t('UPLOAD_FAILED');
          app.error = true;
        }
      });
    },
    async uploadByUrlWithPrompt() {
      const url = await this.$prompt(this.$t('PLEASE_PROVIDE_URL_TO_IPA'));
      await this.uploadByUrl(url);
    },
    async tryUploadByUrlWithQuery() {
      const url = this.$route.query.importUrl;
      if (!url) {
        return;
      }

      this.$router.replace({ path: this.$route.fullPath, query: { importUrl: undefined } });
      await this.uploadByUrl(url);

      this.$notify({
        type: 'success',
        text: this.$t('APP_IMPORT_STARTED'),
      });
    },
    async uploadByUrl(url) {
      try {
        this.uploadingByUrl = true;
        await this.$api.uploadAppByUrl({ url, id: this.generateId() });
        this.tryScheduleGetJobs();
      } finally {
        this.uploadingByUrl = false;
      }
    },
    deleteJob(id) {
      this.$api.deleteAppJob(id).then(() => {
        const index = this.uploads.findIndex(u => u.id === id);
        this.uploads.splice(index, 1);
      });
    },
    getJobs() {
      return this.$api.getAppsJobs().then((jobs) => {
        jobs.forEach((newJob) => {
          const oldJob = this.uploads.find(u => u.id === newJob.id);
          const date = new Date(newJob.updated_at * 1000).toLocaleString();
          const error = /error/i.test(newJob.status);

          if (!oldJob) {
            this.uploads.push({
              id: newJob.id, status: newJob.status, date, finished: newJob.is_finished, error,
            });
            return;
          }

          oldJob.status = newJob.status;
          oldJob.date = date;
          oldJob.error = error;
          if (newJob.is_finished && !oldJob.finished) {
            oldJob.finished = true;
            this.loadFirstSearchPage();
            this.getCustomerProfile();
          }
        });
      });
    },
    async tryScheduleGetJobs() {
      // eslint-disable-next-line no-underscore-dangle
      if (this._isDestroyed) {
        return;
      }

      try {
        await this.getJobs();
      } finally {
        if (this.shouldScheduleGetJobs) {
          setTimeout(() => {
            this.tryScheduleGetJobs();
          }, 1000);
        }
      }
    },
    onDeleted(app) {
      const idx = this.search.result.findIndex(({ id }) => app.id === id);
      if (idx !== -1) {
        this.search.result.splice(idx, 1);
        this.search.pagination.pageSizeDelta -= 1;
      }

      this.getCustomerProfile();
    },
  },
  mounted() {
    this.getCustomerProfile();
    this.tryScheduleGetJobs();
    this.tryUploadByUrlWithQuery();

    this.$api.getStats().then((response) => {
      this.stats = response;
    });

    this.$api.getCustomerProfile();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
  flex-grow: 1;

  &__app {
    padding: 10px 0;
    border-bottom: 1px solid #e5e5e5;
  }
}

.email {
  display: block;
  font-weight: 600;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e5e5;
  margin-top: 20px;

  &__title {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 16px;
  }

  &__storage {
    background: var(--lighter-primary);
    border-radius: 2px;
    padding: 3px;
  }
}

.row {
  display: flex;

  @include notebook {
    flex-direction: column-reverse;
  }
}

.upload {
  flex: 0 0 200px;
  margin-left: 40px;

  @include notebook {
    margin: 0 auto 20px;
  }

  &__input {
    opacity: 0;
    position: absolute;
    z-index: -1;
  }

  &__box {
    background: var(--bg);
    border: 1px solid #ebebeb;
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding: 15px;
  }

  &__button {
    display: block;
    background: $primary;
    border-radius: 2px;
    color: #fff;
    font-size: 14px;
    padding: 7px;
    width: 100%;

    & + .upload__button {
      margin-top: 10px;
    }
  }

  &__num {
    display: block;
    text-align: center;
    font-size: 12px;
    margin: 10px 0;
  }

  &__item {
    border-top: 1px solid #e5e5e5;
    padding: 10px 0;

    &:last-child {
      padding-bottom: 0;
    }
  }

  &__title {
    font-size: 12px;
    font-weight: 500;
    word-break: break-word;
  }

  &__progress-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $text-secondary;
    font-size: 12px;
    margin-top: 4px;
  }

  &__progress {
    flex: 0 0 130px;
    height: 3px;
    position: relative;
    background: #d8d8d8;
    border-radius: 2px;

    &-inner {
      height: 3px;
      position: absolute;
      background: $primary;
      border-radius: 2px;
      width: 0;
    }
  }

  &__status {
    display: block;
    word-break: break-word;
    background: var(--bg-success);
    border-radius: 2px;
    color: #3c4f3e;
    font-size: 12px;
    padding: 3px;
    margin: 6px 0;

    &_error {
      background: var(--bg-error);
      color: var(--configure-error-text);
    }
  }

  &__date {
    display: flex;
    justify-content: space-between;
    color: $text-secondary;
    font-size: 10px;
  }

  &__delete {
    margin-left: 5px;
    cursor: pointer;
  }
}

.help {
  font-size: 12px;
  margin: 10px 10px 0;
  padding-top: 10px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  align-items: flex-start;

  &__icon {
    margin-top: 3px;
    margin-right: 5px;
  }
}

.filters {
  margin-top: 15px;
}

.loading-spinner {
  margin-top: 15px;
  opacity: 0;

  &_visible {
    opacity: 1;
  }
}
</style>
